import { Button, Card, Col, Input, Row, Select, Typography } from "antd";

const { Text } = Typography;

/**
 * Controlled component for Form.Item — manages a list of items (activities or
 * what_we_do) where each item has { name, [description], media }.
 *
 * Props (injected by Form.Item):
 *   value    – [{ name, description?, media }]
 *   onChange – called with the updated array
 *
 * Own props:
 *   masterItems     – raw master list from the API
 *   nameKey         – key to read the display name from each master item
 *   loading         – whether master data is still loading
 *   placeholder     – select placeholder text
 *   showDescription – whether to show the editable description field (default true)
 */
function ItemListSelector({
  value = [],
  onChange,
  masterItems = [],
  nameKey,
  loading,
  placeholder,
  showDescription = true,
}) {
  const selectedNames = new Set(value.map((v) => v.name));

  const availableOptions = masterItems
    .filter((item) => !selectedNames.has(item[nameKey]))
    .map((item) => ({ value: item[nameKey], label: item[nameKey] }));

  const handleAdd = (name) => {
    const masterItem = masterItems.find((m) => m[nameKey] === name);
    const newItem = { name, media: masterItem?.media ?? [] };
    if (showDescription) newItem.description = masterItem?.description ?? "";
    onChange([...value, newItem]);
  };

  const handleRemove = (name) => {
    onChange(value.filter((v) => v.name !== name));
  };

  const handleDescriptionChange = (name, description) => {
    onChange(value.map((v) => (v.name === name ? { ...v, description } : v)));
  };

  return (
    <>
      <Select
        placeholder={placeholder}
        options={availableOptions}
        loading={loading}
        value={null}
        onChange={handleAdd}
        style={{ width: "100%", marginBottom: value.length ? 12 : 0 }}
        showSearch
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />

      {value.map((item) => {
        const masterItem = masterItems.find((m) => m[nameKey] === item.name);
        const imageUrl = masterItem?.media?.[0]?.value ?? item.media?.[0]?.value;
        const descriptionMissing = showDescription && !item.description?.trim();

        return (
          <Card
            key={item.name}
            size="small"
            style={{ marginBottom: 8, borderRadius: 8 }}
            styles={{ body: { padding: "10px 12px" } }}
          >
            <Row gutter={10} align="middle" style={{ marginBottom: showDescription ? 8 : 0 }}>
              {imageUrl && (
                <Col>
                  <img
                    src={imageUrl}
                    alt={item.name}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 6,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Col>
              )}
              <Col flex={1}>
                <Text strong>{item.name}</Text>
              </Col>
              <Col>
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={() => handleRemove(item.name)}
                >
                  ✕
                </Button>
              </Col>
            </Row>

            {showDescription && (
              <>
                <Input.TextArea
                  value={item.description}
                  onChange={(e) => handleDescriptionChange(item.name, e.target.value)}
                  placeholder="Brief description (required)…"
                  rows={2}
                  maxLength={500}
                  showCount
                  status={descriptionMissing ? "error" : ""}
                />
                {descriptionMissing && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    Description is required.
                  </Text>
                )}
              </>
            )}
          </Card>
        );
      })}
    </>
  );
}

export default ItemListSelector;
